import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

const CHART_COLORS = ['#0052CC', '#36B37E', '#FF8B00', '#6554C0', '#00B8D9', '#FF5630', '#FFAB00', '#172B4D'];

const TrendIcon = ({ trend }) => {
    if (trend === "up") return <TrendUp className="h-4 w-4 text-green-600" weight="bold" />;
    if (trend === "down") return <TrendDown className="h-4 w-4 text-red-600" weight="bold" />;
    if (trend === "flat") return <Minus className="h-4 w-4 text-muted-foreground" weight="bold" />;
    return null;
};

export const StatBlock = ({ items = [] }) => (
    <div className="grid grid-cols-2 gap-2">
        {items.map((it, i) => (
            <Card key={i} className="border bg-white dark:bg-slate-900">
                <CardContent className="p-3">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">{it.label}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-lg font-bold truncate">{it.value}</p>
                        <TrendIcon trend={it.trend} />
                    </div>
                    {it.hint && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{it.hint}</p>}
                </CardContent>
            </Card>
        ))}
    </div>
);

export const TableBlock = ({ columns = [], rows = [], caption }) => (
    <Card className="border overflow-hidden">
        <div className="overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((c, i) => (
                            <TableHead key={i} className={i === 0 ? "" : "text-right"}>{c}</TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row, ri) => (
                        <TableRow key={ri}>
                            {row.map((cell, ci) => (
                                <TableCell key={ci} className={ci === 0 ? "font-medium" : "text-right"}>{cell}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
        {caption && <p className="text-[11px] text-muted-foreground px-3 py-2 border-t">{caption}</p>}
    </Card>
);

export const ChartBlock = ({ chartType = "bar", title, data = [], series }) => {
    // Default to a single "value" series if none specified.
    const keys = (series && series.length ? series : [{ key: "value", label: "Value" }]).filter(
        (s) => s && s.key
    );
    if (!data.length) return null;

    return (
        <Card className="border">
            <CardContent className="p-3">
                {title && <p className="text-xs font-medium mb-2">{title}</p>}
                <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        {chartType === "pie" ? (
                            <PieChart>
                                <Pie data={data} dataKey={keys[0].key} nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                                    {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        ) : chartType === "line" ? (
                            <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip />
                                {keys.length > 1 && <Legend />}
                                {keys.map((s, i) => (
                                    <Line key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key}
                                        stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
                                ))}
                            </LineChart>
                        ) : (
                            <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip />
                                {keys.length > 1 && <Legend />}
                                {keys.map((s, i) => (
                                    <Bar key={s.key} dataKey={s.key} name={s.label || s.key}
                                        fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[3, 3, 0, 0]} />
                                ))}
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

const TextBlock = ({ content }) => (
    <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed
        prose-p:my-1 prose-ul:my-1 prose-headings:my-1 prose-table:text-xs">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ""}</ReactMarkdown>
    </div>
);

// Renders one assistant message described as an array of typed blocks.
export default function MessageBlocks({ blocks = [] }) {
    return (
        <div className="space-y-3">
            {blocks.map((block, i) => {
                if (!block || typeof block !== "object") return null;
                switch (block.type) {
                    case "heading":
                        return <h3 key={i} className="text-sm font-semibold">{block.content}</h3>;
                    case "text":
                        return <TextBlock key={i} content={block.content} />;
                    case "stat":
                        return <StatBlock key={i} items={block.items} />;
                    case "table":
                        return <TableBlock key={i} columns={block.columns} rows={block.rows} caption={block.caption} />;
                    case "chart":
                        return <ChartBlock key={i} chartType={block.chartType} title={block.title} data={block.data} series={block.series} />;
                    default:
                        // Forward-compatible: unknown block types fall back to text if possible.
                        return block.content ? <TextBlock key={i} content={String(block.content)} /> : null;
                }
            })}
        </div>
    );
}
